<template>
  <div class="mt-2 px-6 hris-job-profiles-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div
      v-if="!libraryLoading"
    >
      <NoDataBlock
        v-if="!jobProfiles.length"
        :label="authData.hrisName"
        document-type="Job Profiles"
      >
        <template #description>
          There is no Job Profile data available for your selection.
        </template>
      </NoDataBlock>
      <div v-else>
        <StickyListHeader
          v-if="jobProfiles.length"
          class="hris-list-item-wrapper"
          document-class="item-job-profile-hris-add-checkbox"
          :header-columns="headerColumns"
          :selected-sort="selectedSort"
          :select-all="allSelected"
          :sort-direction="sortDirection"
          checkbox-size="md"
          @submit-sort="submitSort"
          @update:select-all="handleSelectAll"
        />
        <LibraryList>
          <LibraryListItemWrapper
            v-for="{
              ulid,
              title,
              job_level: jobLevel,
              job_families: jobFamilies,
              hris_job_code: hrisJobCode,
              effective_date: effectiveDate,
            } in jobProfiles"
            :key="ulid"
            class="hris-list-item-wrapper"
          >
            <router-link
              :to="{ query: { preview: ulid, type: 'jobProfileHris' } }"
            >
              <HrisJobProfilesItem
                :ulid="ulid"
                :title="title"
                :job-level="formatJobLevel(jobLevel, { mode: 'short' })"
                :job-family="joinJobFamilies(jobFamilies)"
                :job-code="hrisJobCode"
                :effective-on="effectiveDate"
                :selected="documentIsSelected(ulid)"
                show-checkbox
                class="item-job-profile-hris-add-checkbox"
                @update:selected="(value) => handleCheckboxActions(ulid, value)"
              />
            </router-link>
          </LibraryListItemWrapper>
        </LibraryList>
      </div>
    </div>

    <StickyPagination
      v-if="totalPages > 1"
      :model-value="page"
      :length="totalPages"
      @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router })"
    />
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppBase } from '@/stores/app/base';
import { useAppRoute } from '@/stores/app/route';
import { useFilters } from '@/stores/filters/filters';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { useAppPagination } from '@/stores/app/pagination';
import { useSubMenu } from '@/stores/filters/subMenu';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useCompareSelectionPrune } from '@/composables/useCompareSelectionPrune';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import LibraryListItemWrapper from '@/components/shared/LibraryListItemWrapper.vue';
import LibraryList from '@/components/shared/LibraryList.vue';
import HrisJobProfilesItem from '@/components/jobProfiles/profileListItems/HrisJobProfilesItem.vue';
import StickyPagination from '@/components/shared/StickyPagination.vue';
import StickyListHeader from '@/components/library/StickyListHeader.vue';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import { stickyHeadersJobProfiles, getStickyHeaderSortColumnLabel } from '@/helpers/constants/stickyHeaders';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppAuthData } from '@/stores/app/authData';
import { getJpHrisLibraryPageName } from '@/helpers/jobProfileLibraryTracking';
import { formatJobLevel } from '@/helpers/jobLevelDisplay';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { authData } = storeToRefs(useAppAuthData());
const hrisLibraryPageName = computed(() => getJpHrisLibraryPageName(authData.value.hrisName));
const { previousRoute } = storeToRefs(useAppRoute());

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { jobProfiles, selectedProfiles, textFilter } = storeToRefs(useJobProfiles());
const { loadJobProfiles, resetSelectedProfiles, setTextFilter } = useJobProfiles();
const {
  loadFilters,
  setFilterConfig,
  sendFilterRequest,
  submitSubMenu,
} = useFilters();
const { showFilterBarSettings } = useAppBase();
const { totalPages, page } = storeToRefs(useAppPagination());
const { selectedSubMenuOption } = storeToRefs(useSubMenu());
const { setSelectedSubMenuOption } = useSubMenu();
const { pruneSelectionIfFiltersChanged } = useCompareSelectionPrune();

const headerColumns = computed(() => {
  const hrisHeaders = [
    'select',
    'title',
    'jobCode',
    'jobFamily',
    'level',
    'effectiveOn',
  ];

  return hrisHeaders.map((header) => stickyHeadersJobProfiles[header]);
});

const selectedSort = computed(() => {
  const fallback = route.query?.sort || 'effective_date';
  return (selectedSubMenuOption.value || fallback).replace('-', '');
});

const sortDirection = computed(() => (
  (selectedSubMenuOption.value || route.query?.sort || 'effective_date').startsWith('-')
    ? 'desc'
    : 'asc'
));

const allSelected = computed(() => false);

const documentIsSelected = (ulid) => selectedProfiles.value.has(ulid);

const joinJobFamilies = (families) => families
  .map((family) => family.name)
  .join(', ');

const submitSort = (selectedId) => {
  const current = selectedSubMenuOption.value || route.query?.sort || '-effective_date';
  const currentBase = current.replace('-', '');

  if (selectedId === '__select') return;

  let optionId;

  if (selectedId === currentBase) {
    optionId = current.startsWith('-') ? selectedId : `-${selectedId}`;
  } else {
    optionId = selectedId;
  }

  const direction = optionId.startsWith('-') ? 'desc' : 'asc';

  useTracking().jpLibSortedTable({
    page: hrisLibraryPageName.value,
    column: getStickyHeaderSortColumnLabel(optionId, stickyHeadersJobProfiles),
    direction,
  });

  submitSubMenu({ optionId, router });
};

const handleCheckboxActions = (ulid) => {
  if (selectedProfiles.value.has(ulid)) {
    selectedProfiles.value.delete(ulid);
  } else selectedProfiles.value.add(ulid);
};

const handleSelectAll = (value) => {
  if (!value) {
    selectedProfiles.value = new Set();

    return;
  }

  selectedProfiles.value = new Set(
    jobProfiles.value.map(({ ulid }) => ulid),
  );
};

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const loadPage = async (params) => {
  const searchTerm = textFilter.value || params.searchTerm;
  const paramsWithSearch = {
    ...params,
    searchTerm,
  };

  await loadJobProfiles(paramsWithSearch);
  await loadFilters({ ...paramsWithSearch, sort: selectedSubMenuOption.value });

  selectedProfiles.value = pruneSelectionIfFiltersChanged(selectedProfiles.value, {
    searchTerm,
    selectedFilters: params.selectedFilters,
    visibleUlids: jobProfiles.value.map(({ ulid }) => ulid),
  });

  if (params?.routeQuery?.type === 'jobProfileHris' && params?.routeQuery?.preview) {
    router.push({ query: { preview: params.routeQuery.preview, type: 'jobProfileHris' } });
  }
};

onMounted(async () => {
  if (previousRoute.value?.name !== 'job-architecture-compare') {
    resetSelectedProfiles();
  }

  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'jobProfiles',
    endpoint: 'jobProfileLibrary',
    showMyJobs: false,
    datepicker: false,
  });

  showFilterBarSettings({ route: route.name });

  if (route.query?.q) setTextFilter(route.query.q);

  await loadPage({
    routeQuery: route.query,
    router,
    page: route.query?.page || 1,
    mode: '',
    searchTerm: route.query?.q || '',
    selectedMyJobs: route.query?.owned,
  });

  emit('track-page-load', { page: `${authData.value.hrisName} Job Profiles: Open` });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);

  setFilterConfig({
    subMenuType: null,
  });

  if (route.name !== 'job-architecture-compare') {
    resetSelectedProfiles();
  }

  setSelectedSubMenuOption('');
  setTextFilter('');
});
</script>

<style lang="less" scoped>
.hris-list-item-wrapper {
  display: grid;
  grid-template-columns: 1fr;
}

.item-job-profile-hris, :deep(.item-job-profile-hris) {
  grid-template-columns: minmax(150px, 1fr) minmax(80px, 120px) minmax(150px, 250px) 120px 120px;
}

.item-job-profile-hris-add-checkbox, :deep(.item-job-profile-hris-add-checkbox) {
  grid-template-columns: 50px minmax(150px, 1fr) minmax(80px, 120px) minmax(150px, 250px) 120px 120px;
}

:deep(.item-job-profile-hris-add-checkbox) .select-all-header {
  padding-left: 0;
  margin: 0 auto;
}

:deep(.item-job-profile-hris.sticky-header-items),
:deep(.item-job-profile-hris .document-title) {
  padding-left: 16px;
}
</style>
